/*
 * HomeFixer360 - Partner Application Form
 * Copyright (c) 2025 Guy T. Dunn Sr. / Idea Guy Brands Incorporated
 * All Rights Reserved.
 * 
 * This software is proprietary and confidential. Unauthorized copying, distribution,
 * modification, or use is strictly prohibited.
 * 
 * For licensing inquiries: guy@ideaguybrands.com
 */

import React, { useState } from 'react';
import { ArrowLeft, Handshake, Building, Users, DollarSign, CheckCircle, Upload, Globe, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import Navigation from '../components/landing/Navigation';

const PartnerApplication = () => {
  const [formData, setFormData] = useState({
    // Contact Information
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    
    // Company Information
    companyName: '',
    website: '',
    companySize: '',
    yearEstablished: '',
    businessType: '',
    
    // Partner Type
    partnerType: '',
    primaryFocus: '',
    targetMarket: '',
    
    // Experience & Capabilities
    roofingExperience: '',
    salesExperience: '',
    techExperience: '',
    currentCustomers: '',
    monthlyLeads: '',
    
    // Marketing & Sales
    marketingChannels: [],
    salesApproach: '',
    revenueGoals: '',
    
    // Additional Information
    whyPartner: '',
    additionalInfo: ''
  });

  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCheckboxChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter(item => item !== value)
        : [...prev[field], value]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Map form data to backend API structure
      const submissionData = {
        // Company Information
        company_name: formData.companyName,
        company_website: formData.website,
        company_size: formData.companySize,
        years_in_business: formData.yearEstablished ? new Date().getFullYear() - parseInt(formData.yearEstablished) : 0,
        industry_type: formData.businessType === 'roofing-contractor' ? 'Home Service Pro' : 
                      formData.businessType === 'consultant' ? 'Consultant' :
                      formData.businessType === 'software-company' ? 'Software Vendor' :
                      formData.businessType === 'marketing-agency' ? 'Sales Agency' : 'Other',
        business_description: `${formData.businessType} with ${formData.roofingExperience} of roofing experience. ${formData.additionalInfo}`,
        
        // Contact Information
        primary_contact_name: `${formData.firstName} ${formData.lastName}`,
        primary_contact_email: formData.email,
        primary_contact_phone: formData.phone,
        primary_contact_title: 'Partner Applicant',
        
        // Partnership Details
        partner_type: formData.partnerType,
        expected_monthly_referrals: parseInt(formData.monthlyLeads) || 1,
        target_market: formData.targetMarket || 'Not specified',
        existing_customer_base: parseInt(formData.currentCustomers) || 0,
        
        // Technical Capabilities
        has_development_team: formData.businessType === 'software-company',
        api_integration_experience: formData.techExperience || 'Not specified',
        existing_software_integrations: formData.techExperience || 'Not specified',
        
        // Marketing & Sales
        marketing_channels: formData.marketingChannels.map(channel => channel.toLowerCase().replace(/\s+/g, '_')),
        sales_process_description: `Sales experience: ${formData.salesExperience}. Marketing through: ${formData.marketingChannels.join(', ')}. ${formData.salesApproach}`,
        current_partnerships: 'Not specified',
        
        // Additional Information
        why_partner: formData.whyPartner,
        additional_comments: formData.additionalInfo,
        
        // Qualification Questions
        min_deal_size: null,
        average_sales_cycle: formData.salesExperience === '0-2' ? '< 30 days' : 
                            formData.salesExperience === '3-5' ? '1-3 months' :
                            formData.salesExperience === '6-10' ? '3-6 months' : '6+ months',
        has_sales_team: formData.monthlyLeads > 20
      };

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/partners/apply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submissionData)
      });

      const result = await response.json();

      if (response.ok) {
        setSubmitted(true);
        // Store submission result for display
        window.partnerSubmissionResult = result;
      } else {
        throw new Error(result.detail || 'Failed to submit application');
      }
    } catch (error) {
      console.error('Submission error:', error);
      alert(`Error submitting application: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = formData.firstName && formData.lastName && formData.email && 
                     formData.companyName && formData.partnerType;

  if (submitted) {
    const submissionResult = window.partnerSubmissionResult || {};
    
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full p-8 text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Application Submitted Successfully!</h1>
          
          {submissionResult.id && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-6">
              <h3 className="font-semibold text-green-800 mb-2">{submissionResult.message}</h3>
              <div className="text-sm text-green-700 space-y-2 text-left">
                <p><strong>Application ID:</strong> {submissionResult.id}</p>
                <p><strong>Status:</strong> {submissionResult.status}</p>
                <p><strong>Estimated Review Time:</strong> {submissionResult.estimated_review_time}</p>
              </div>
            </div>
          )}
          
          <p className="text-gray-600 mb-6 leading-relaxed">
            Thank you {formData.firstName} for your interest in becoming a HomeFixer360 partner. 
            Our partner team will review your application and contact you soon.
          </p>
          
          <div className="bg-blue-50 rounded-lg p-6 mb-6">
            <h3 className="font-semibold text-blue-900 mb-2">What's Next?</h3>
            <div className="text-blue-800 text-sm text-left">
              {submissionResult.next_steps && <p className="mb-3">{submissionResult.next_steps}</p>}
              <ul className="space-y-1">
                <li>• Check your email for confirmation details</li>
                <li>• Our partnership team will contact you soon</li>
                <li>• Partner agreement and onboarding process</li>
                <li>• Access to partner portal and resources</li>
              </ul>
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={() => window.location.href = '/partner-program'}
              variant="outline"
              className="px-8 py-3"
            >
              Back to Partner Program
            </Button>
            <Button 
              onClick={() => window.location.href = '/'}
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3"
            >
              Return to Homepage
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <Navigation />
      
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-6 py-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Partner Application</h1>
            <p className="text-gray-600">Join the HomeFixer360 partner ecosystem</p>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Progress Header */}
        <div className="bg-gradient-to-r from-blue-600 to-green-500 rounded-xl p-6 mb-8 text-white">
          <div className="flex items-center space-x-3 mb-4">
            <Handshake className="w-8 h-8" />
            <h2 className="text-2xl font-bold">Partner Application Form</h2>
          </div>
          <p className="text-blue-100">
            Complete this application to join our partner program and start earning recurring commissions.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Contact Information */}
          <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-100">
            <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
              <Mail className="w-6 h-6 text-blue-600 mr-3" />
              Contact Information
            </h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  First Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={formData.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Last Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={formData.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  required
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Company Information */}
          <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-100">
            <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
              <Building className="w-6 h-6 text-green-600 mr-3" />
              Company Information
            </h3>
            <div className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Company Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.companyName}
                    onChange={(e) => handleInputChange('companyName', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Company Website
                  </label>
                  <input
                    type="url"
                    value={formData.website}
                    onChange={(e) => handleInputChange('website', e.target.value)}
                    placeholder="https://yourcompany.com"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              
              <div className="grid md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Company Size</label>
                  <select
                    value={formData.companySize}
                    onChange={(e) => handleInputChange('companySize', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select Size</option>
                    <option value="1-5">1-5 employees</option>
                    <option value="6-20">6-20 employees</option>
                    <option value="21-50">21-50 employees</option>
                    <option value="51+">51+ employees</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Year Established</label>
                  <input
                    type="number"
                    value={formData.yearEstablished}
                    onChange={(e) => handleInputChange('yearEstablished', e.target.value)}
                    placeholder="2020"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Business Type</label>
                  <select
                    value={formData.businessType}
                    onChange={(e) => handleInputChange('businessType', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select Type</option>
                    <option value="roofing-contractor">Home Service Pro</option>
                    <option value="consultant">Roofing Consultant</option>
                    <option value="software-company">Software Company</option>
                    <option value="marketing-agency">Marketing Agency</option>
                    <option value="distributor">Distributor/Reseller</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Partner Type Selection */}
          <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-100">
            <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
              <Handshake className="w-6 h-6 text-purple-600 mr-3" />
              Partnership Type
            </h3>
            
            <div className="space-y-4 mb-6">
              {[
                { 
                  value: 'referral', 
                  title: 'Referral Partner', 
                  description: '30% recurring commission on referred customers',
                  commission: '30% recurring'
                },
                { 
                  value: 'reseller', 
                  title: 'Reseller Partner', 
                  description: 'White-label and resell under your brand',
                  commission: '60% recurring'
                }
              ].map((type) => (
                <label key={type.value} className="block">
                  <div className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                    formData.partnerType === type.value 
                      ? 'border-blue-500 bg-blue-50' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}>
                    <div className="flex items-start space-x-3">
                      <input
                        type="radio"
                        name="partnerType"
                        value={type.value}
                        checked={formData.partnerType === type.value}
                        onChange={(e) => handleInputChange('partnerType', e.target.value)}
                        className="mt-1 text-blue-600 focus:ring-blue-500"
                      />
                      <div className="flex-1">
                        <div className="flex justify-between items-start">
                          <div>
                            <h4 className="font-semibold text-gray-900">{type.title}</h4>
                            <p className="text-gray-600 text-sm">{type.description}</p>
                          </div>
                          <div className="text-right">
                            <div className="text-green-600 font-semibold">{type.commission}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Experience & Capabilities */}
          <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-100">
            <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
              <Users className="w-6 h-6 text-orange-600 mr-3" />
              Experience & Capabilities
            </h3>
            
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Roofing Industry Experience
                </label>
                <select
                  value={formData.roofingExperience}
                  onChange={(e) => handleInputChange('roofingExperience', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Experience</option>
                  <option value="0-2">0-2 years</option>
                  <option value="3-5">3-5 years</option>
                  <option value="6-10">6-10 years</option>
                  <option value="11+">11+ years</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sales Experience
                </label>
                <select
                  value={formData.salesExperience}
                  onChange={(e) => handleInputChange('salesExperience', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Experience</option>
                  <option value="0-2">0-2 years</option>
                  <option value="3-5">3-5 years</option>
                  <option value="6-10">6-10 years</option>
                  <option value="11+">11+ years</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Current Customer Base
                </label>
                <input
                  type="number"
                  value={formData.currentCustomers}
                  onChange={(e) => handleInputChange('currentCustomers', e.target.value)}
                  placeholder="Number of active customers"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Monthly Leads/Prospects
                </label>
                <input
                  type="number"
                  value={formData.monthlyLeads}
                  onChange={(e) => handleInputChange('monthlyLeads', e.target.value)}
                  placeholder="Average monthly leads"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Marketing Channels (Select all that apply)
              </label>
              <div className="grid md:grid-cols-3 gap-3">
                {[
                  'Email Marketing',
                  'Social Media',
                  'Trade Shows',
                  'Direct Sales',
                  'Referral Network',
                  'Content Marketing',
                  'Paid Advertising',
                  'Partnerships',
                  'Other'
                ].map((channel) => (
                  <label key={channel} className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.marketingChannels.includes(channel)}
                      onChange={() => handleCheckboxChange('marketingChannels', channel)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">{channel}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Goals & Motivation */}
          <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-100">
            <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
              <DollarSign className="w-6 h-6 text-green-600 mr-3" />
              Goals & Motivation
            </h3>
            
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Why do you want to become a HomeFixer360 partner?
                </label>
                <textarea
                  value={formData.whyPartner}
                  onChange={(e) => handleInputChange('whyPartner', e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Tell us about your interest in partnering with HomeFixer360..."
                />
              </div>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Monthly Revenue Goals
                  </label>
                  <select
                    value={formData.revenueGoals}
                    onChange={(e) => handleInputChange('revenueGoals', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select Goal</option>
                    <option value="1000-2500">$1,000 - $2,500</option>
                    <option value="2500-5000">$2,500 - $5,000</option>
                    <option value="5000-10000">$5,000 - $10,000</option>
                    <option value="10000+">$10,000+</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Target Market
                  </label>
                  <input
                    type="text"
                    value={formData.targetMarket}
                    onChange={(e) => handleInputChange('targetMarket', e.target.value)}
                    placeholder="e.g., Small home service pros in Texas"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Additional Information
                </label>
                <textarea
                  value={formData.additionalInfo}
                  onChange={(e) => handleInputChange('additionalInfo', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Any additional information you'd like to share..."
                />
              </div>
            </div>
          </div>

          {/* Submit */}
          <div className="bg-gradient-to-r from-blue-600 to-green-500 rounded-xl p-8 text-center">
            <h3 className="text-2xl font-bold text-white mb-4">Ready to Submit Your Application?</h3>
            <p className="text-blue-100 mb-6">
              Our partner team will review your application and contact you within 48 hours.
            </p>
            
            <Button
              type="submit"
              disabled={!isFormValid || isSubmitting}
              size="lg"
              className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-4 text-lg font-semibold disabled:opacity-50"
            >
              {isSubmitting ? (
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                  <span>Submitting Application...</span>
                </div>
              ) : (
                'Submit Partner Application'
              )}
            </Button>
            
            <p className="text-blue-100 text-sm mt-4">
              By submitting this application, you agree to our Partner Terms and Conditions.
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PartnerApplication;